import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useRecordConsent } from '@/hooks/useRecordConsent';
import type { UserProfile } from '@/hooks/useProfile';
import type { DashboardNavigationState } from '@/types/navigationState';

interface UsePostAuthFlowParams {
  profile: UserProfile | null;
  profileLoading: boolean;
}

/**
 * Hook to handle post-authentication flows like guest data migration
 * and proper onboarding redirects
 */
export const usePostAuthFlow = ({ profile, profileLoading }: UsePostAuthFlowParams) => {
  const { user, loading: authLoading } = useAuth();
  const { isTrialExpired, isPremium, loading: subscriptionLoading } = useSubscription();
  const recordConsent = useRecordConsent();
  const navigate = useNavigate();
  const location = useLocation();

  // Finalise click-wrap consent for OAuth sign-ups: the AuthModal sets
  // `pending_signup_consent` before the Google round-trip; once we land
  // back here with a real user, write the receipt and clear the flag.
  useEffect(() => {
    if (!user) return;
    let pending = false;
    try {
      pending = sessionStorage.getItem('pending_signup_consent') === '1';
    } catch {
      return;
    }
    if (!pending) return;
    void recordConsent({ context: 'signup', userId: user.id });
    try {
      sessionStorage.removeItem('pending_signup_consent');
    } catch {
      // ignore
    }
  }, [user, recordConsent]);


  useEffect(() => {
    // Wait for both auth and profile to finish loading
    if (authLoading || profileLoading || subscriptionLoading || !user) return;

    // Profile hasn't loaded yet for this user — wait for it
    if (!profile) return;

    const handlePostAuthFlow = async () => {
      // Check if user just completed OAuth (Google sign-in)
      const isOAuthReturn = sessionStorage.getItem('oauth_return') === 'true';
      
      // Handle onboarding flow
      if (!profile.onboarding_completed) {
        // Redirect to onboarding from landing page OR after OAuth
        if (location.pathname === '/' || isOAuthReturn) {
          sessionStorage.removeItem('oauth_return');
          navigate('/onboarding', { replace: true });
        }
      } else {
        // User is fully onboarded
        sessionStorage.removeItem('oauth_return');

        // B8: Redirect expired trials to /subscription/expired
        // Only if they haven't explicitly chosen Explorer (checked via localStorage flag)
        const hasChosenExplorer = localStorage.getItem('chosen_explorer') === 'true';
        if (isTrialExpired && !isPremium && !hasChosenExplorer) {
          if (location.pathname !== '/subscription/expired' && location.pathname !== '/subscription') {
            navigate('/subscription/expired', { replace: true });
            return;
          }
        }

        if (location.pathname === '/' || location.pathname === '/onboarding') {
          navigate('/dashboard', {
            replace: true,
            state: { startTour: true } satisfies DashboardNavigationState,
          });
        }
      }
    };

    handlePostAuthFlow();
  }, [user, profile, authLoading, profileLoading, subscriptionLoading, isTrialExpired, isPremium, navigate, location.pathname]);

  return {
    isReady: !authLoading && !profileLoading && !!user
  };
};